import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import breakpoints from "assets/theme/base/breakpoints";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import BulkUploadSubscriptions from "./components/BulkUploadSubscriptions";
import CreateSubscription from "./components/CreateSubscriptions";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CancelIcon from "@mui/icons-material/Cancel";
import ExploreOffIcon from "@mui/icons-material/ExploreOff";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PendingIcon from "@mui/icons-material/Pending";
import * as XLSX from "xlsx";
import {
  getSubscriptionServiceFee,
  getSubscriptionStatistics,
  getSubscriptionsByPlaceId,
} from "../../store/slice/subscriptions/subscriptionSlice";
import Statistics from "./components/Statistics";
import SubscriptionsTable from "./components/SubscriptionTable";
import { isEmpty, sortBy } from "lodash";
import moment from "moment";
import SubscriptionEmailSendDialog from "./components/SubscriptionReminderEmailDIalog";
import { FormControl } from "@mui/material";
import SubscriptionSearchBar from "./components/SubscriptionSearchBar";
import { amountToShow } from "global/functions";
import DownloadIcon from "@mui/icons-material/Download";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";
import Tooltip from "@mui/material/Tooltip";
import CustomTooltip from "components/Common/CustomTooltip";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import MDBadge from "components/MDBadge";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <MDBox sx={{ p: 3 }}>{children}</MDBox>}
    </div>
  );
}

const tabInfo = {
  active: "Subscriptions currently active and within their valid date range.",
  paused: "Subscriptions currently paused by user or admin.",
  pendingPayment: "Subscriptions awaiting payment completion (initialised).",
  canceled: "Subscriptions canceled before or after start.",
  failed: "Subscriptions with failed payment attempts.",
  expired: "Subscriptions whose end date has passed.",
  pending: "Invitee subscriptions pending verification or acceptance.",
  upcoming:
    "Subscriptions that will renew within the next 3 days (auto-renew).",
  actionRequired:
    "Subscriptions requiring operator action, such as reactivation requests, pending license plates, or failed payments.",
};

function Subscriptions() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);

  const userData = useSelector((state) => state.users?.meInfo);
  const roleModules = userData?.roleModules || {};
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab");
  const [tabValue, setTabValue] = useState(activeTab ? activeTab : "active");
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  const isLoading = useSelector((state) => state.subscriptions.loading);
  const subscriptionsList = useSelector((state) => state.subscriptions.list);
  const statistics = useSelector((state) => state.subscriptions.statistics);
  const serviceFee = useSelector(
    (state) => state.subscriptions?.subscriptionServiceFee,
  );
  const currentDate = moment();
  let placeId = selectedPlace?._id;
  if (!placeId) {
    placeId = localStorage.getItem("placeId");
  }

  useEffect(() => {
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);
    queryParams.set("tab", newValue);
    const updatedQueryString = queryParams.toString();
    navigate(`?${updatedQueryString}`);
  };

  useEffect(() => {
    dispatch(getSubscriptionsByPlaceId(placeId));
    dispatch(getSubscriptionServiceFee(placeId));
    dispatch(getSubscriptionStatistics(placeId));
  }, [dispatch, placeId, activeTab]);

  // compute upcomingRenewalData derived set when needed
  const computeUpcomingRenewalData = (list) => {
    if (isEmpty(list)) return [];
    return list.filter(
      (item) =>
        item.subscriptionStatus === "active" &&
        item.isAutoRenew &&
        !item.isSubscriptionPaused &&
        moment(item.endDate).isBetween(
          moment(),
          moment().add(3, "days"),
          "day",
          "[]",
        ),
    );
  };

  // Logic to filter Action Required items
  // Logic to filter Action Required items
  const computeActionRequiredData = (list) => {
    if (isEmpty(list)) return [];
    return list?.filter((item) => {
      // EXCLUDE FAILED AND CANCELED DATA (EXCEPT REACTIVATION REQUESTS)
      if (item?.subscriptionStatus === "failed") {
        return false; 
      }
      
      // Exclude canceled subscriptions that do not have reactivation requests, but include those that do (handled in reactivation logic below)
      if (item?.subscriptionStatus === "cancel" && !item?.isReactivationRequested) {
        return false; 
      }

      // Reactivation & Status-based Actions
      const needsReactivation = item?.isReactivationRequested;
      const isPaused = item?.isSubscriptionPaused; // Include Paused
      const isPendingPayment = item?.subscriptionStatus === "initialize"; // Include Pending

      // License Plate Management
      const hasPendingPlates = item?.licensePlate?.some((p) => p.status === 1);
      const hasPlateUpdates = item?.licensePlateGetUpdated;

      // Payment & Renewal Failures
      const isFailed = item?.subscriptionStatus === "failed";

      // Expired Auto-Renewals: Auto-renew subscriptions that have passed their end date without renewing
      const isExpiredAutoRenew =
        item?.isAutoRenew &&
        !item?.isSubscriptionPaused &&
        moment().isAfter(moment(item?.endDate)) &&
        item?.subscriptionStatus !== "pending" &&
        item?.subscriptionStatus !== "initialize";

      // Upcoming Renewals (Within 3 days)
      const isUpcoming =
        item?.subscriptionStatus === "active" &&
        item?.isAutoRenew &&
        moment(item?.endDate).isBetween(
          moment(),
          moment().add(3, "days"),
          "day",
          "[]",
        );

      return (
        needsReactivation ||
        isPaused ||
        isPendingPayment ||
        hasPendingPlates ||
        hasPlateUpdates ||
        isFailed ||
        isExpiredAutoRenew ||
        isUpcoming
      );
    });
  };

  const actionRequiredData = computeActionRequiredData(filteredSubscriptions);

  const downloadReport = (data) => {
    // create a local upcomingRenewalData copy based on passed data (so export matches current tab exactly)
    const upcomingRenewalData = computeUpcomingRenewalData(data);

    const filteredSubscriptionsLocal = data.filter((item) => {
      const startDate = moment(item.startDate);
      const endDate = moment(item.endDate);
      const isExpired = currentDate.isAfter(endDate);

      switch (tabValue) {
        case "active":
          return (
            ((item.subscriptionStatus === "active" ||
              item.subscriptionStatus === "cancel") &&
              !item.isSubscriptionPaused &&
              currentDate.isBetween(
                startDate,
                endDate.add(1, "days"),
                null,
                "[]",
              )) ||
            (item.subscriptionStatus === "active" &&
              startDate.isAfter(currentDate) &&
              endDate.isAfter(currentDate))
          );
        case "paused":
          return item.isSubscriptionPaused;
        case "pendingPayment":
          return item.subscriptionStatus === "initialize";
        case "canceled":
          return item.subscriptionStatus === "cancel";
        case "upcoming":
          return upcomingRenewalData.includes(item);
        case "expired":
          return (
            !item.isSubscriptionPaused &&
            isExpired &&
            item.subscriptionStatus !== "pending" &&
            item.subscriptionStatus !== "initialize"
          );
        case "pending":
          return item.subscriptionStatus === "pending";
        case "failed":
          return item.subscriptionStatus === "failed";
        case "actionRequired":
          return actionRequiredData.includes(item);
        default:
          return false;
      }
    });

    const columnMapping = {
      "Subscription ID": "subscriptionNumber",
      "Start Date": "startDate",
      "End Date": "endDate",
      "License Plate": "licensePlate",
      Email: "customerId.email",
      Mobile: "customerId.mobile",
      Name: "customerId.fullName",
      "Subscription Type": "isMonthly",
      "Auto Renew": "isAutoRenew",
      "Payment Method": "paymentMethodType",
      "Total Amount": "totalAmount",
      "Base Rate": "baseRate",
      Tax: "tax",
      "Service Fee": "serviceFee",
      "Merchant Fee": "paymentGatewayFee",
      "Dock Revenue": "dockRevenue",
      "Owner Payout": "ownerPayout",
      "Pro-Rate TotalAmount": "firstMonthTotalAmount",
      "Pro-Rate BaseRate": "firstMonthBaseRate",
      "Pro-Rate Tax": "firstMonthTax",
      "Pro-Rate ServiceFee": "firstMonthServiceFee",
      "Pro-Rate MerchentFee": "firstMonthPaymentGatewayFee",
      "Pro-Rate Dock Revenue": "firstMonthDockRevenue",
      "Pro-Rate Owner Payout": "firstMonthTotalAmount",
    };

    const filteredData = filteredSubscriptionsLocal.map((item) => {
      const filteredItem = {};
      Object.keys(columnMapping).forEach((columnName) => {
        const key = columnMapping[columnName];
        let value = item[key];
        if (key === "customerId.fullName") {
          const firstName =
            item.customerId && item.customerId.firstName
              ? item.customerId.firstName
              : "";
          const lastName =
            item.customerId && item.customerId.lastName
              ? item.customerId.lastName
              : "";
          value = `${firstName} ${lastName}`.trim();
        } else if (key === "startDate" || key === "endDate") {
          value = value ? new Date(value).toLocaleDateString("en-US") : "";
        } else if (key === "licensePlate") {
          value = value
            ? value.map((plate) => plate.licensePlateNumber).join(", ")
            : "";
        } else if (key === "customerId.email") {
          value =
            item["customerId"] && item["customerId"]["email"]
              ? item["customerId"]["email"]
              : "";
        } else if (key === "customerId.mobile") {
          value =
            item["customerId"] && item["customerId"]["mobile"]
              ? item["customerId"]["mobile"]
              : item["customerId"] && item["customerId"]["secondaryMobile"]
                ? item["customerId"]["secondaryMobile"]
                : "";
        } else if (
          [
            "baseRate",
            "tax",
            "serviceFee",
            "paymentGatewayFee",
            "dockRevenue",
            "ownerPayout",
            "totalAmount",
            "firstMonthBaseRate",
            "firstMonthServiceFee",
            "firstMonthTax",
            "firstMonthTotalAmount",
            "firstMonthOwnerPayout",
            "firstMonthDockRevenue",
            "firstMonthApplicationFee",
            "firstMonthPaymentGatewayFee",
          ].includes(key)
        ) {
          value = value ? `$${amountToShow(value)}` : `$0`;
        } else if (key === "paymentMethodType") {
          value =
            value === "card" ? "Credit Card" : value === "ACH" ? "ACH" : value;
        } else if (key === "isMonthly") {
          value = value === true ? "Monthly" : "Custom";
        } else if (key === "isAutoRenew") {
          value = value === true ? "Yes" : "No";
        }
        filteredItem[columnName] = value;
      });
      return filteredItem;
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);

    const columnWidths = Object.keys(columnMapping).map((columnName) => {
      switch (columnName) {
        case "Subscription ID":
          return { wch: 15 };
        case "Start Date":
        case "End Date":
          return { wch: 15 };
        case "Base Rate":
        case "Tax":
        case "Service Fee":
        case "Total Amount":
          return { wch: 10 };
        case "License Plate":
          return { wch: 30 };
        default:
          return { wch: 25 };
      }
    });
    ws["!cols"] = columnWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const fileName =
      selectedPlace && selectedPlace.parkingCode
        ? `${selectedPlace.parkingCode}.xlsx`
        : "subscriptions.xlsx";
    XLSX.writeFile(wb, fileName);
  };

  const upcomingRenewalData = computeUpcomingRenewalData(filteredSubscriptions);
  const pendingReminderCount = upcomingRenewalData.filter(
    (obj) => !obj.isReminderEmailSend,
  ).length;
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <MDBox pt={2} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={1}
                  mt={-2}
                  py={1}
                  px={1}
                  variant="gradient"
                  bgColor={sidenavColor}
                  borderRadius="lg"
                  coloredShadow={sidenavColor}
                  className="d-flex align-items-center gap-2"
                >
                  <MDTypography
                    variant="h6"
                    color="white"
                    className="flex-grow-1"
                  >
                    Subscriptions
                  </MDTypography>

                  {roleModules.Subscription_add && placeId && (
                    <>
                      <Tooltip title="View subscription statistics" arrow>
                        <MDButton
                          variant="outlined"
                          size="small"
                          onClick={() => navigate("/subscriptions/statistics")}
                        >
                          Statistics
                        </MDButton>
                      </Tooltip>

                      <Tooltip title="Create new subscription" arrow>
                        <MDButton
                          variant="outlined"
                          size="small"
                          onClick={() => setDialogOpen(true)}
                        >
                          Create
                        </MDButton>
                      </Tooltip>
                    </>
                  )}
                </MDBox>

                <MDBox p={2}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                      <PlacePicker />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      {/* optional filter placeholder */}
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <SubscriptionSearchBar
                          subscriptionsList={subscriptionsList}
                          setFilteredSubscriptions={setFilteredSubscriptions}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Tooltip
                        title="Export data for the currently selected tab only"
                        arrow
                      >
                        <MDButton
                          color={sidenavColor}
                          variant="outlined"
                          size="small"
                          onClick={() => downloadReport(filteredSubscriptions)}
                        >
                          Export <DownloadIcon />
                        </MDButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox p={2}>
                  <Statistics data={statistics} type="active" />
                </MDBox>

                <MDBox p={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                      <AppBar position="static">
                        <Tabs
                          orientation={tabsOrientation}
                          value={tabValue}
                          onChange={handleSetTabValue}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            "& .MuiTab-root": {
                              paddingX: 2,
                              flexShrink: 0,
                            },
                          }}
                        >
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Active
                                <CustomTooltip
                                  id="active"
                                  title={tabInfo.active}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <DirectionsCarIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="active"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Paused
                                <CustomTooltip
                                  id="paused"
                                  title={tabInfo.paused}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <PauseCircleIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="paused"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Pending Payment
                                <CustomTooltip
                                  id="pendingPayment"
                                  title={tabInfo.pendingPayment}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <PendingIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="pendingPayment"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Canceled
                                <CustomTooltip
                                  id="canceled"
                                  title={tabInfo.canceled}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <CancelIcon fontSize="small" sx={{ mt: -0.25 }} />
                            }
                            value="canceled"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Failed Payment
                                <CustomTooltip
                                  id="failed"
                                  title={tabInfo.failed}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <MoneyOffIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="failed"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Expired
                                <CustomTooltip
                                  id="expired"
                                  title={tabInfo.expired}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <ExploreOffIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="expired"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Invitee's
                                <CustomTooltip
                                  id="pending"
                                  title={tabInfo.pending}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <PendingIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="pending"
                          />
                          <Tab
                            label={
                              <MDBox
                                position="relative"
                                display="flex"
                                alignItems="center"
                              >
                                <MDTypography
                                  sx={{ fontSize: "16px", mr: 0.5 }}
                                >
                                  Upcoming renewal
                                </MDTypography>

                                {pendingReminderCount > 0 && (
                                  <MDBadge
                                    badgeContent={pendingReminderCount}
                                    color="error"
                                    size="xs"
                                    circular
                                    sx={{ ml: 1 }}
                                  />
                                )}

                                <CustomTooltip
                                  id="upcoming"
                                  title={tabInfo.upcoming}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <PublishedWithChangesRoundedIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="upcoming"
                          />
                          <Tab
                            label={
                              <MDBox display="flex" alignItems="center">
                                Action Required
                                {actionRequiredData.length > 0 && (
                                  <MDBadge
                                    badgeContent={actionRequiredData.length}
                                    color="error"
                                    size="xs"
                                    circular
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                <CustomTooltip
                                  id="actionRequired"
                                  title={tabInfo.actionRequired}
                                  openTooltipId={openTooltipId}
                                  setOpenTooltipId={setOpenTooltipId}
                                  iconVariant="outlined"
                                  iconSize={16}
                                />
                              </MDBox>
                            }
                            icon={
                              <ReportProblemIcon
                                fontSize="small"
                                sx={{ mt: -0.25 }}
                              />
                            }
                            value="actionRequired"
                          />
                        </Tabs>
                      </AppBar>

                      <TabPanel value={tabValue} index="active">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? sortBy(
                                  filteredSubscriptions.filter((item) => {
                                    const startDate = moment(item.startDate);
                                    const endDate = moment(item.endDate)
                                      .clone()
                                      .add(1, "days");
                                    return (
                                      ((item.subscriptionStatus === "active" ||
                                        item.subscriptionStatus === "cancel") &&
                                        !item.isSubscriptionPaused &&
                                        currentDate.isBetween(
                                          startDate,
                                          endDate,
                                          null,
                                          "[]",
                                        )) ||
                                      (item.subscriptionStatus === "active" &&
                                        startDate.isAfter(currentDate) &&
                                        endDate.isAfter(currentDate))
                                    );
                                  }),
                                  (item) => !item.licensePlateGetUpdated,
                                )
                              : []
                          }
                          subscriptionType="active"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="paused">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? filteredSubscriptions.filter(
                                  (item) => item.isSubscriptionPaused,
                                )
                              : []
                          }
                          subscriptionType="paused"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="pendingPayment">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? filteredSubscriptions.filter(
                                  (item) =>
                                    item.subscriptionStatus === "initialize",
                                )
                              : []
                          }
                          subscriptionType="paymentPending"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="canceled">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? filteredSubscriptions.filter(
                                  (item) =>
                                    item.subscriptionStatus === "cancel",
                                )
                              : []
                          }
                          subscriptionType="canceled"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="failed">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? filteredSubscriptions.filter(
                                  (item) =>
                                    item.subscriptionStatus === "failed",
                                )
                              : []
                          }
                          subscriptionType="failed"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="expired">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? filteredSubscriptions.filter((item) => {
                                  const endDate = moment(item.endDate);
                                  return (
                                    !item.isSubscriptionPaused &&
                                    currentDate.isAfter(endDate) &&
                                    item.subscriptionStatus !== "pending" &&
                                    item.subscriptionStatus !== "initialize"
                                  );
                                })
                              : []
                          }
                          subscriptionType="expired"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="pending">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={
                            !isEmpty(filteredSubscriptions)
                              ? filteredSubscriptions.filter(
                                  (item) =>
                                    item.subscriptionStatus === "pending",
                                )
                              : []
                          }
                          subscriptionType="pending"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="upcoming">
                        <MDBox display="flex" justifyContent="end">
                          <Tooltip
                            title="Send reminder emails for upcoming renewals"
                            arrow
                          >
                            <MDButton
                              size="small"
                              variant="contained"
                              color={sidenavColor}
                              onClick={() => setEmailDialogOpen(true)}
                            >
                              Send Reminder Email
                            </MDButton>
                          </Tooltip>
                        </MDBox>
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={upcomingRenewalData}
                          subscriptionType="upcoming"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>

                      <TabPanel value={tabValue} index="actionRequired">
                        <SubscriptionsTable
                          isLoading={isLoading}
                          subscriptionList={actionRequiredData}
                          subscriptionType="actionRequired"
                          placeData={selectedPlace}
                          roleModules={roleModules}
                        />
                      </TabPanel>
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox pt={3}>
                  <CreateSubscription
                    placeId={placeId}
                    placeData={selectedPlace}
                    dialogOpen={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    serviceFee={serviceFee}
                    setTabValue={setTabValue}
                  />
                  <BulkUploadSubscriptions
                    placeId={placeId}
                    placeData={selectedPlace}
                    dialogOpen={bulkDialogOpen}
                    onClose={() => setBulkDialogOpen(false)}
                  />
                </MDBox>

                <SubscriptionEmailSendDialog
                  placeId={placeId}
                  subscriptionList={upcomingRenewalData}
                  dialogOpen={emailDialogOpen}
                  onClose={() => setEmailDialogOpen(false)}
                />
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Subscriptions;
